import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { Suspense, lazy, useEffect, useState } from 'react';
import { HashRouter, Navigate, Route, Routes, useLocation } from 'react-router';
import { AppShell } from './components/AppShell';
import { ListSkeleton } from './components/ui/Skeleton';
import { ToastProvider, useToast } from './components/ui/Toast';
import { ApiError, authenticate, hasStoredSession, onAuthLost } from './lib/api';
import { errorMessage } from './lib/errors';
import { useI18n } from './lib/i18n';
import { LiveProvider } from './lib/live';
import { useBootstrap } from './lib/queries';
import { SessionProvider } from './lib/session';
import type { TelegramBridge } from './lib/telegram';
import { GateScreen, type GateReason } from './screens/GateScreen';
import { OrdersScreen } from './screens/OrdersScreen';

// Первый экран — список заказов, он нужен сразу. Остальное подгружаем по
// требованию: так первый рендер в WebView остаётся быстрым.
const OrderScreen = lazy(() =>
  import('./screens/OrderScreen').then((module) => ({ default: module.OrderScreen })),
);
const NewOrderScreen = lazy(() =>
  import('./screens/NewOrderScreen').then((module) => ({ default: module.NewOrderScreen })),
);
const EditOrderScreen = lazy(() =>
  import('./screens/EditOrderScreen').then((module) => ({ default: module.EditOrderScreen })),
);
const ProductsScreen = lazy(() =>
  import('./screens/ProductsScreen').then((module) => ({ default: module.ProductsScreen })),
);
const CustomersScreen = lazy(() =>
  import('./screens/CustomersScreen').then((module) => ({ default: module.CustomersScreen })),
);
const CustomerScreen = lazy(() =>
  import('./screens/CustomerScreen').then((module) => ({ default: module.CustomerScreen })),
);
const FinanceScreen = lazy(() =>
  import('./screens/FinanceScreen').then((module) => ({ default: module.FinanceScreen })),
);
const SettingsScreen = lazy(() =>
  import('./screens/SettingsScreen').then((module) => ({ default: module.SettingsScreen })),
);

/**
 * Вход — такой же запрос к серверу, как и остальные, поэтому живёт в том же
 * слое данных: ни эффектов, ни ручного состояния загрузки. Повтор — это
 * refetch, а отказ по белому списку — обычная ошибка с понятным кодом.
 */
function useSignIn(initDataRaw: string | null) {
  return useQuery({
    queryKey: ['auth', initDataRaw],
    queryFn: () => authenticate(initDataRaw ?? ''),
    enabled: Boolean(initDataRaw),
    // Повторять вход бессмысленно: initData либо принимают, либо нет.
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function App({ bridge }: { bridge: TelegramBridge }) {
  const signIn = useSignIn(bridge.initDataRaw);
  const [lost, setLost] = useState<GateReason | null>(null);

  // Сессию могли отозвать на сервере, пока приложение открыто. Это обратный
  // вызов внешней подписки, а не тело эффекта — состояние здесь менять уместно.
  useEffect(
    () =>
      onAuthLost((reason) => setLost(reason === 'telegram_id_not_allowed' ? 'denied' : 'error')),
    [],
  );

  const retry = () => {
    setLost(null);
    void signIn.refetch();
  };

  if (!bridge.initDataRaw) return <GateScreen reason="no-telegram" />;
  if (lost) return <GateScreen reason={lost} onRetry={retry} />;
  if (signIn.isPending) return <Splash />;

  if (signIn.isError) {
    if (signIn.error instanceof ApiError && signIn.error.code === 'telegram_id_not_allowed') {
      return <GateScreen reason="denied" />;
    }
    // Сервер не ответил, но прошлая сессия жива — пробуем работать на ней:
    // refresh-токен сам поднимет доступ на первом же запросе.
    if (!hasStoredSession()) {
      return (
        <GateScreen
          reason="error"
          message={signIn.error instanceof Error ? signIn.error.message : undefined}
          onRetry={retry}
        />
      );
    }
  }

  return (
    <ToastProvider>
      <Workspace />
    </ToastProvider>
  );
}

function Splash() {
  return (
    <div className="px-4 pt-16">
      <ListSkeleton count={4} />
    </div>
  );
}

/** Рабочая часть: доступ подтверждён, осталось загрузить, что показывать. */
function Workspace() {
  const bootstrap = useBootstrap();
  const { t } = useI18n();
  const toast = useToast();

  if (bootstrap.isPending) return <Splash />;

  if (bootstrap.isError || !bootstrap.data) {
    return (
      <GateScreen
        reason="error"
        message={errorMessage(bootstrap.error, t)}
        onRetry={() => void bootstrap.refetch()}
      />
    );
  }

  return (
    <SessionProvider bootstrap={bootstrap.data}>
      <LiveProvider
        enabled
        onNewOrder={(event) => toast.show(`${t('orders.newOrder')} №${event.number}`, 'success')}
      >
        <HashRouter>
          <AppShell>
            <AnimatedRoutes />
          </AppShell>
        </HashRouter>
      </LiveProvider>
    </SessionProvider>
  );
}

/**
 * Переходы между экранами. Анимируем только opacity и transform — всё
 * остальное на слабом телефоне заметно подтормаживает.
 */
function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, x: 12 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -8 }}
        transition={{ duration: 0.16, ease: 'easeOut' }}
        className="gpu"
      >
        <Suspense fallback={<Splash />}>
          <Routes location={location}>
            <Route path="/" element={<Navigate to="/orders" replace />} />
            <Route path="/orders" element={<OrdersScreen />} />
            {/* Статический путь объявлен до параметра: «new» не должно
                попасть в карточку заказа как идентификатор. */}
            <Route path="/orders/new" element={<NewOrderScreen />} />
            <Route path="/orders/:ref/edit" element={<EditOrderScreen />} />
            <Route path="/orders/:ref" element={<OrderScreen />} />
            <Route path="/products" element={<ProductsScreen />} />
            <Route path="/customers" element={<CustomersScreen />} />
            <Route path="/customers/:ref" element={<CustomerScreen />} />
            <Route path="/finance" element={<FinanceScreen />} />
            <Route path="/settings" element={<SettingsScreen />} />
            <Route path="*" element={<Navigate to="/orders" replace />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}
